import { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const particles = Array.from({ length: 34 }, (_, index) => ({
  id: index,
  left: `${(index * 29) % 100}%`,
  delay: `${(index % 9) * 0.6}s`,
  size: `${5 + (index % 5) * 2}px`,
  duration: `${14 + (index % 8)}s`,
}));

export function BackgroundEffects() {
  const [audioEnabled, setAudioEnabled] = useState(false);
  const cursorRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);

  useEffect(() => {
    const moveCursor = (event: PointerEvent) => {
      if (!cursorRef.current) return;
      cursorRef.current.style.transform = `translate3d(${event.clientX}px, ${event.clientY}px, 0)`;
    };

    window.addEventListener('pointermove', moveCursor);
    return () => window.removeEventListener('pointermove', moveCursor);
  }, []);

  useEffect(() => {
    if (!audioEnabled) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('button, a, input, select, textarea')) return;
      const context = audioRef.current;
      if (!context) return;

      const osc = context.createOscillator();
      const gain = context.createGain();
      osc.frequency.value = 520;
      gain.gain.setValueAtTime(0.035, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.08);
      osc.connect(gain).connect(context.destination);
      osc.start();
      osc.stop(context.currentTime + 0.08);
    };

    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [audioEnabled]);

  const toggleAudio = async () => {
    if (audioEnabled) {
      oscillatorRef.current?.stop();
      oscillatorRef.current?.disconnect();
      gainRef.current?.disconnect();
      oscillatorRef.current = null;
      gainRef.current = null;
      setAudioEnabled(false);
      return;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const context = audioRef.current || new AudioContextClass();
    audioRef.current = context;
    await context.resume();

    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 128;
    gain.gain.value = 0.012;
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillatorRef.current = oscillator;
    gainRef.current = gain;
    setAudioEnabled(true);
  };

  return (
    <>
      <div className="ambient-grid" aria-hidden="true" />
      <div className="light-orbit light-orbit-one" aria-hidden="true" />
      <div className="light-orbit light-orbit-two" aria-hidden="true" />
      <div className="neon-lines" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <div className="particle-layer" aria-hidden="true">
        {particles.map((particle) => (
          <span
            key={particle.id}
            style={{
              left: particle.left,
              width: particle.size,
              height: particle.size,
              animationDelay: particle.delay,
              animationDuration: particle.duration,
            }}
          />
        ))}
      </div>
      <div className="cursor-glow" ref={cursorRef} aria-hidden="true" />
      <button
        className="audio-toggle"
        type="button"
        onClick={toggleAudio}
        aria-label={audioEnabled ? 'Disable background sound' : 'Enable background sound'}
      >
        {audioEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
      </button>
    </>
  );
}
